// ============================================================================
// CHANGO V5.0 — SKU 品牌矩阵显示 (§9.3 + §9.6.3 最终版, 2026-04-27 §9 重定)
// ============================================================================
// 真实来源:
//   §9.3 9 套餐 + 1 加购 = 10 SKU
//   §9.3.1 套餐功能 / 场景 / 适配企业 (双栏 A 系蓝 / B 系绿)
//   §9.5.1 ops 套餐管理 UI 颜色规范
//
// 与后端 code/server/config/sku-layers.js 同源, 任何一边改了都要同步。
// ============================================================================

/** §9.6.3 所有套餐通用席位文案 */
export const FREE_SEATS_TEXT = '6 免费席位（1老板 + 2财务 + 3业务员）';
export const PAID_SEAT_TEXT = '超出 ¥999/席/年';
export const SEAT_COMBINED_TEXT = `${FREE_SEATS_TEXT} · ${PAID_SEAT_TEXT}`;

/**
 * §9.5.1 ops 后台颜色规范: A 系蓝 / B 系绿 / 加购橙
 * 这套色值贯穿 ops + tenant 所有跟套餐相关的展示, 看一眼就知道客户类型。
 */
export const SERIES_COLORS = Object.freeze({
  A: {
    badge: 'bg-blue-100 text-blue-800 border-blue-300',
    card:  'border-blue-300 bg-blue-50',
    accent: 'text-blue-700',
    dot:   'bg-blue-500'
  },
  B: {
    badge: 'bg-emerald-100 text-emerald-800 border-emerald-300',
    card:  'border-emerald-300 bg-emerald-50',
    accent: 'text-emerald-700',
    dot:   'bg-emerald-500'
  },
  '_': {  // 加购
    badge: 'bg-amber-100 text-amber-800 border-amber-300',
    card:  'border-amber-300 bg-amber-50',
    accent: 'text-amber-700',
    dot:   'bg-amber-500'
  }
});

/**
 * @typedef {{
 *   sku_code: string,
 *   displayCode: string|null,    // 'A1' / 'A2' / ... / 'B4' / null (extra_seat)
 *   shortName: string,            // 紧凑显示 "A1 工厂版"
 *   fullName: string,             // 完整显示 (单工作区 + 席位)
 *   layers: string[],
 *   moduleCount: number,
 *   hasELayer: boolean,
 *   series: 'A'|'B'|'_',
 *   seriesIdx: number,
 *   maxWorkspaces: number|null,
 *   description: string,          // 一句话定位
 *   useCase: string[],            // 解决场景 (3-5 条)
 *   targetCompany: string         // 适配企业类型
 * }} SkuBrandInfo
 */

function pkg({ sku_code, displayCode, name, layers, moduleCount, hasELayer,
              series, seriesIdx, maxWorkspaces, description, useCase, targetCompany }) {
  const wsLabel = maxWorkspaces == null ? '—' : '单工作区';
  return Object.freeze({
    sku_code,
    displayCode,
    shortName: name,
    fullName: `${name}（${wsLabel} · ${SEAT_COMBINED_TEXT}）`,
    layers,
    moduleCount,
    hasELayer,
    series,
    seriesIdx,
    maxWorkspaces,
    description,
    useCase,
    targetCompany
  });
}

/** @type {Record<string, SkuBrandInfo>} */
export const SKU_BRAND_MAP = Object.freeze({
  // ── A 系（含工厂模块 A）─────────────────────────────────────────────────
  sku_a1: pkg({
    sku_code: 'sku_a1', displayCode: 'A1', name: 'A1 工厂版',
    layers: ['A'], moduleCount: 1, hasELayer: false,
    series: 'A', seriesIdx: 1, maxWorkspaces: 1,
    description: '工厂主体，自产自销 / 自产 FOB 出口，单底盘',
    useCase: [
      '算清自产产品的真实成本',
      '给国内 B2B 客户报 EXW 价',
      '给外贸公司或国外采购商报 FOB 价'
    ],
    targetCompany: '纯国内工厂，没有自营外贸公司，不直接出口（出口由下游外贸公司或国外采购商负责）。典型：江浙沪、广东的中小型代工厂。'
  }),
  sku_a2: pkg({
    sku_code: 'sku_a2', displayCode: 'A2', name: 'A2 工贸一体版',
    layers: ['A','B'], moduleCount: 2, hasELayer: false,
    series: 'A', seriesIdx: 2, maxWorkspaces: 1,
    description: '既有工厂又有外贸公司，自产 + 外采双路径',
    useCase: [
      '既有自产产能也做外采，同一订单可比价',
      '集团内部"工厂卖给外贸公司"的内部交易记录',
      '工厂淡季外采补单不影响利润口径一致性'
    ],
    targetCompany: '自有工厂 + 自有外贸公司，工贸一体集团。典型：江浙沪小型出口集团（年出口千万级），既自产也代采。'
  }),
  sku_a3: pkg({
    sku_code: 'sku_a3', displayCode: 'A3', name: 'A3 工贸一体跨境版',
    layers: ['A','B','C'], moduleCount: 3, hasELayer: false,
    series: 'A', seriesIdx: 3, maxWorkspaces: 1,
    description: '自产 + 外采 + 平台电商',
    useCase: [
      '自产产品直接走亚马逊 / 独立站',
      '同时保留 B2B 出口和外采业务',
      '自有 SKU 在跨境电商和 B2B 渠道分别测算毛利'
    ],
    targetCompany: '工贸一体 + 自营跨境电商。典型：有工厂的亚马逊卖家、品牌出海集团（DTC + B2B 双渠道）。'
  }),
  sku_a4: pkg({
    sku_code: 'sku_a4', displayCode: 'A4', name: 'A4 工贸一体集团版',
    layers: ['A','B','E'], moduleCount: 3, hasELayer: true,
    series: 'A', seriesIdx: 4, maxWorkspaces: 1,
    description: '自产 + 外采 + 多主体合规',
    useCase: [
      '工厂主体 + 外贸主体 + 海外销售主体之间的转移定价',
      '集团合并报表 / 多地税务合规',
      '内部交易留痕，避免税务稽查问题'
    ],
    targetCompany: '工贸一体 + 多法律主体。典型：已有海外子公司的成熟工贸集团。'
  }),
  sku_a5: pkg({
    sku_code: 'sku_a5', displayCode: 'A5', name: 'A5 全栈旗舰版',
    layers: ['A','B','C','E'], moduleCount: 4, hasELayer: true,
    series: 'A', seriesIdx: 5, maxWorkspaces: 1,
    description: '4 模块全开，工贸一体 + 跨境 + 集团',
    useCase: [
      '完整产业链：自产 + 外采 + B2B 出口 + 跨境电商 + 多主体合规',
      '一份成本底盘支撑所有渠道和主体',
      '集团 IPO / 融资财报合并'
    ],
    targetCompany: '成熟出口集团：自有工厂 + 自营外贸 + 自营跨境电商 + 多法律主体。通常已有数千万至上亿出口规模。'
  }),
  // ── B 系（仅外贸模块 B，无工厂）────────────────────────────────────────
  sku_b1: pkg({
    sku_code: 'sku_b1', displayCode: 'B1', name: 'B1 外贸版',
    layers: ['B'], moduleCount: 1, hasELayer: false,
    series: 'B', seriesIdx: 1, maxWorkspaces: 1,
    description: '外贸公司主体，采购出口，单底盘',
    useCase: [
      '从其他工厂或贸易商采购货物',
      '出口报价含完整出口环节费用 + 退税',
      '给海外客户报 CIF / DDP 价'
    ],
    targetCompany: '纯外贸公司，自己不生产，从国内工厂采购后出口。典型：传统外贸 B2B 公司、SOHO 外贸。'
  }),
  sku_b2: pkg({
    sku_code: 'sku_b2', displayCode: 'B2', name: 'B2 外贸跨境版',
    layers: ['B','C'], moduleCount: 2, hasELayer: false,
    series: 'B', seriesIdx: 2, maxWorkspaces: 1,
    description: '外贸 + 平台电商（亚马逊 / Shopify / 独立站等）',
    useCase: [
      '除了 B2B 出口，还自营亚马逊 / Shopify / 独立站等',
      '算清"在亚马逊定价 X 美元能不能赚"',
      '跨境电商和 B2B 用同一套成本底盘，避免左手倒右手算不清'
    ],
    targetCompany: '外贸公司 + 自营跨境电商。典型：亚马逊卖家、独立站卖家、转型中的传统外贸公司。'
  }),
  sku_b3: pkg({
    sku_code: 'sku_b3', displayCode: 'B3', name: 'B3 外贸集团版',
    layers: ['B','E'], moduleCount: 2, hasELayer: true,
    series: 'B', seriesIdx: 3, maxWorkspaces: 1,
    description: '外贸 + 多主体合规（境内外多公司、转移定价、集团合并）',
    useCase: [
      '多主体之间内部交易定价',
      '境内外利润归集 / 按主体看利润',
      '海外仓 / 海外公司的税务合规',
      '集团报表合并'
    ],
    targetCompany: '有 ≥2 个法律主体的外贸集团。典型：大陆外贸公司 + 香港离岸 + 海外销售实体的避税 / 资金回流结构。'
  }),
  sku_b4: pkg({
    sku_code: 'sku_b4', displayCode: 'B4', name: 'B4 外贸跨境集团版',
    layers: ['B','C','E'], moduleCount: 3, hasELayer: true,
    series: 'B', seriesIdx: 4, maxWorkspaces: 1,
    description: '外贸 + 平台电商 + 多主体合规',
    useCase: [
      '大陆外贸公司 + 香港离岸 + 海外仓 + 亚马逊 / Shopify / 独立站全链路',
      '跨境电商收款分账 / 多主体税务',
      '集团报表合并'
    ],
    targetCompany: '跨境电商集团（无自有工厂）+ 多主体合规需求。典型：DTC 品牌出海公司、有海外仓的亚马逊大卖。'
  }),
  // ── 加购 ──────────────────────────────────────────────────────────────
  extra_seat: Object.freeze({
    sku_code: 'extra_seat', displayCode: null, shortName: '加购席位',
    fullName: '加购席位 · ¥999/席/年（按主账号剩余天数折算）',
    layers: [], moduleCount: 0, hasELayer: false,
    series: '_', seriesIdx: 0, maxWorkspaces: null,
    description: '与所有 9 套餐正交的席位扩容',
    useCase: [
      '老板免费 1 + 2 财务 + 3 业务员 = 6 席位用满后扩容',
      '按主账号剩余天数折算金额，与主账号同到期'
    ],
    targetCompany: '任意套餐均可加购，与套餐组合无关。'
  })
});

/** §9.3 9 套餐展示顺序 (A1→A5 然后 B1→B4) + extra_seat 末尾 */
export const PACKAGE_ORDER = Object.freeze([
  'sku_a1', 'sku_a2', 'sku_a3', 'sku_a4', 'sku_a5',
  'sku_b1', 'sku_b2', 'sku_b3', 'sku_b4',
  'extra_seat'
]);

/** A 系 (含工厂) — 5 个 */
export const A_SERIES = Object.freeze(['sku_a1', 'sku_a2', 'sku_a3', 'sku_a4', 'sku_a5']);
/** B 系 (仅外贸) — 4 个 */
export const B_SERIES = Object.freeze(['sku_b1', 'sku_b2', 'sku_b3', 'sku_b4']);

/**
 * 取得某 SKU 的品牌信息。未知 SKU 退回到一个安全的占位（绝不抛错），
 * 但会在控制台留下一条警告以便发现 SKU 漂移。
 * @param {string} skuCode
 * @returns {SkuBrandInfo}
 */
export function brandForSku(skuCode) {
  const found = SKU_BRAND_MAP[skuCode];
  if (found) return found;
  if (typeof console !== 'undefined' && console.warn) {
    if (!brandForSku._warned) brandForSku._warned = new Set();
    if (!brandForSku._warned.has(skuCode)) {
      brandForSku._warned.add(skuCode);
      console.warn('[skuBrandVersion] 未知 SKU code（前后端漂移？）:', skuCode);
    }
  }
  return Object.freeze({
    sku_code: skuCode, displayCode: null, shortName: skuCode,
    fullName: skuCode, layers: [], moduleCount: 0, hasELayer: false,
    series: '_', seriesIdx: 0, maxWorkspaces: null,
    description: '未知套餐', useCase: [], targetCompany: ''
  });
}

/**
 * 给 SKU 列表按 §9.3 顺序排序（A1-A5 → B1-B4 → 加购）。
 * @template T
 * @param {T[]} skus
 * @param {(s: T) => string} getCode
 * @returns {T[]}
 */
export function sortByBrand(skus, getCode = (s) => s.sku_code) {
  const orderIdx = (code) => {
    const i = PACKAGE_ORDER.indexOf(code);
    return i === -1 ? 999 : i;
  };
  return [...skus].sort((a, b) => orderIdx(getCode(a)) - orderIdx(getCode(b)));
}

// ----------------------------------------------------------------------------
// §9.4 9 合法子集校验 (前端购物车用; 与后端 sku-layers.js#isLegalSubset 同源)
// ----------------------------------------------------------------------------
const LEGAL_SUBSETS = Object.freeze(new Set([
  'A', 'A,B', 'A,B,C', 'A,B,E', 'A,B,C,E',
  'B', 'B,C', 'B,E', 'B,C,E'
]));

/**
 * 把 layers 数组规范化成 'A,B,C' 格式。
 */
export function normalizeLayers(layers) {
  if (!Array.isArray(layers)) return '';
  return [...new Set(layers.map((s) => String(s).toUpperCase()))]
    .filter((s) => ['A','B','C','E'].includes(s))
    .sort()
    .join(',');
}

/**
 * 校验 layers 是否为 9 合法子集之一。
 */
export function isLegalSubset(layers) {
  const key = normalizeLayers(layers);
  if (!key) return false;
  return LEGAL_SUBSETS.has(key);
}

/**
 * 反查: layers 数组 → SKU 编码 + 中文名。
 */
export function packageNameFromLayers(layers) {
  const key = normalizeLayers(layers);
  for (const code of PACKAGE_ORDER) {
    const sku = SKU_BRAND_MAP[code];
    if (!sku || code === 'extra_seat') continue;
    if (sku.layers.join(',') === key) return sku;
  }
  return null;
}

export default {
  SKU_BRAND_MAP, PACKAGE_ORDER, A_SERIES, B_SERIES, SERIES_COLORS,
  FREE_SEATS_TEXT, PAID_SEAT_TEXT, SEAT_COMBINED_TEXT,
  brandForSku, sortByBrand,
  isLegalSubset, normalizeLayers, packageNameFromLayers
};
